import { createClient } from '@supabase/supabase-js';
import { conLaRegionFija, urlDeFuncion } from './laRutaDeLasFunciones';

/**
 * 🚨 SE LIMPIAN LOS BORDES, Y NO ES PARANOIA — UN SALTO DE LÍNEA ACÁ NOS COSTÓ DÍAS.
 *
 * El secreto `VITE_SUPABASE_ANON_KEY` de GitHub tenía un `\n` al final, de pegarlo en la casilla.
 * Vite hornea el valor tal cual, así que producción salió con una llave de 209 caracteres donde la
 * buena tiene 208. Y el síntoma fue de los peores posibles:
 *
 *   · REST seguía andando. La llave viaja en una CABECERA —ahí el espacio sobrante se descarta— y
 *     la sesión del usuario viaja aparte en `Authorization`. Todo 200. Los datos cargaban.
 *   · El tiempo real moría. Un WebSocket no lleva cabeceras: la llave viaja en la URL
 *     (`?apikey=…%0A`), el salto de línea SÍ cuenta, la firma no valida y el servidor contesta 401.
 *
 * Resultado: el chat cargaba perfecto al refrescar y no llegaba NADA en vivo —ni mensajes, ni
 * «escribiendo», ni «en línea»— porque las tres cosas viajan por ese mismo canal. Y sólo en
 * producción, porque el `.env` de la máquina del founder tiene la llave sin el salto.
 *
 * 🪤 POR QUÉ ACÁ Y NO SÓLO EN EL SECRETO. Arreglar el secreto arregla hoy. Esto arregla también el
 * día que alguien lo vuelva a pegar con un espacio de más.
 */
const limpiar = (v: string | undefined) => (v ?? '').trim();

// Obtener las variables de entorno
const supabaseUrl = limpiar(import.meta.env.VITE_SUPABASE_URL);
const supabaseAnonKey = limpiar(import.meta.env.VITE_SUPABASE_ANON_KEY);

// Validar que las variables de entorno estén configuradas
if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    '❌ Faltan las variables de entorno de Supabase.\n' +
    'Por favor, configura VITE_SUPABASE_URL y VITE_SUPABASE_ANON_KEY en tu archivo .env\n' +
    'Copia .env.example a .env y completa las claves.'
  );
}

/*
 * La llave tiene que ser un JWT de tres partes. Una llave deforme por acá no rompe la pantalla:
 * rompe el tiempo real, sin ningún error visible, y se descubre días después. Mejor gritarlo.
 */
if (supabaseAnonKey.split('.').length !== 3) {
  console.error(
    '❌ VITE_SUPABASE_ANON_KEY no tiene forma de JWT (3 partes separadas por punto). ' +
    'El panel va a cargar datos igual, pero el tiempo real del chat va a fallar con 401.',
  );
}

/**
 * Cliente Singleton de Supabase
 * 
 * IMPORTANTE: Usa siempre este cliente, NO crees múltiples instancias.
 * 
 * Uso:
 * ```typescript
 * import { supabase } from '@/lib/supabase';
 * 
 * const { data, error } = await supabase
 *   .from('products')
 *   .select('*')
 *   .eq('tenant_id', currentTenantId);
 * ```
 */
/**
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * 🚨 LAS EDGE FUNCTIONS CORREN JUNTO A LA BASE, NO JUNTO AL USUARIO
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * Supabase ejecuta cada función en la región más cercana A QUIEN LA LLAMA. Suena bien, y para una
 * función que sólo calcula lo es — pero las nuestras existen para hablar con la base, y la base
 * vive en un solo lugar.
 *
 * Medido el 2026-09-07 desde Lima: la función de envío corría en `sa-east-1` (São Paulo), a 9.500
 * km de la base. Cada consulta suya pagaba ~173 ms de ida y vuelta en vez de ~20, y el camino de un
 * envío hace varias. Por eso mandar se sentía dos o tres veces más lento que recibir: el webhook ya
 * tenía la región fijada en su URL, y el envío nunca se enteró.
 *
 * 🚨 VA EN LA URL Y NO EN UNA CABECERA, Y ESA DISTINCIÓN ROMPIÓ EL ENVÍO UNA VEZ. El primer intento
 * puso `x-region` en las cabeceras globales del cliente. Supabase documenta esa cabecera y funciona
 * — servidor contra servidor. Desde un navegador NO: una cabecera que no esté en la lista de
 * permitidas del preflight hace que el navegador cancele la petición ANTES de mandarla, y nuestras
 * funciones sólo permiten `authorization, x-client-info, apikey, content-type`. Resultado: todos
 * los envíos fallaron con el ícono rojo, sin llegar nunca al servidor.
 *
 * Un parámetro en la URL no pide permiso a nadie. Es el mismo mecanismo que ya usa el webhook.
 *
 * 🪤 Y SE HACE ACÁ, NO EN CADA LLAMADA. Hay 117 archivos que invocan funciones. Un `region:` por
 * invocación sería correcto en cada sitio y nadie lo pondría al agregar el 118: el defecto volvería
 * solo, y en silencio.
 *
 * El costo de fijarla: se pierde el reruteo automático si esa región se cae. No es pérdida real —
 * si `us-west-2` se cae, la base no responde y nada funciona igual.
 */
export { REGION_DE_LA_BASE } from './laRutaDeLasFunciones';

const conLaRegionDeLaBase: typeof fetch = (entrada, init) => {
  const crudo = typeof entrada === 'string' ? entrada
    : entrada instanceof URL ? entrada.href
      : (entrada as Request).url;

  const fija = conLaRegionFija(crudo);
  if (fija !== crudo) {
    // Un `Request` no se puede re-apuntar: se reconstruye conservando método, cuerpo y cabeceras.
    return entrada instanceof Request
      ? fetch(new Request(fija, entrada), init)
      : fetch(fija, init);
  }
  return fetch(entrada as RequestInfo | URL, init);
};

/**
 * Llama a una edge function CON la región de la base fija.
 *
 * 🚨 EXISTE PORQUE EL ENVOLTORIO DE ARRIBA NO ALCANZABA. `conLaRegionDeLaBase` sólo toca lo que
 * pasa por el cliente de Supabase; quien arma la URL a mano y usa `fetch` pelado se saltea la
 * región sin que nada avise. El comentario de acá arriba lo predijo —«nadie lo pondría al agregar
 * el 118»— y pasó: al 09-sep hay 40 llamadas así, y una de ellas le costaba **1085 ms** al lienzo
 * del agente (1710 → 625 ms medido, mediana de 6), porque bloquea el primer pintado esperándola.
 *
 * Usar esto en vez de armar la URL a mano y llamar a `fetch`. El trinquete `limpieza:gate` cuenta
 * las que quedan y no deja aparecer una nueva.
 */
export function fetchDeFuncion(nombre: string, init?: RequestInit): Promise<Response> {
  return conLaRegionDeLaBase(urlDeFuncion(supabaseUrl, nombre), init);
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
  global: { fetch: conLaRegionDeLaBase },
});
